import type { FC, ReactNode } from 'react'
import type { FaqItem } from '@/types'
import type { Tone } from '@/components/generals/info-grid'

import { Plus } from 'lucide-react'
import { useId, useState } from 'react'

import { cn } from '@/lib/utils'
import { SectionLabel } from '@/components/generals/section-label'
import { SplitReveal } from '@/components/generals/split-reveal'
import { isDark, toneClasses } from '@/components/generals/info-grid'

interface FaqSectionProps {
    id: string
    index: string
    label: string
    heading: ReactNode
    intro?: string
    items: FaqItem[]
    tone?: Tone
}

/** Questions and answers as an accessible accordion, one open at a time. */
export const FaqSection: FC<FaqSectionProps> = ({
    id,
    index,
    label,
    heading,
    intro,
    items,
    tone = 'paper'
}): ReactNode => {
    const baseId = useId()
    const [open, setOpen] = useState<number | null>(0)
    const dark = isDark(tone)

    return (
        <section id={id} aria-labelledby={`${id}-heading`} className={cn('relative z-10', toneClasses[tone])}>
            <div className='grid gap-12 px-5 py-24 sm:px-8 lg:grid-cols-12 lg:gap-8 lg:px-12 lg:py-32'>
                <div className='lg:col-span-5'>
                    <div className='lg:sticky lg:top-28'>
                        <SectionLabel index={index} className={dark ? 'text-bone/70' : 'text-charcoal/70'}>
                            {label}
                        </SectionLabel>

                        <SplitReveal
                            as='h2'
                            id={`${id}-heading`}
                            className='mt-6 text-[clamp(2.1rem,4vw,4rem)] font-light leading-[1.02] tracking-[-0.03em] [&_em]:font-serif [&_em]:font-normal'
                        >
                            {heading}
                        </SplitReveal>

                        {intro && (
                            <p data-reveal className={cn('mt-6 max-w-sm text-base leading-relaxed', dark ? 'text-bone/70' : 'text-charcoal/70')}>
                                {intro}
                            </p>
                        )}
                    </div>
                </div>

                <ul className={cn('border-t lg:col-span-7', dark ? 'border-bone/15' : 'border-charcoal/15')}>
                    {items.map((item, itemIndex) => {
                        const expanded = open === itemIndex
                        const buttonId = `${baseId}-question-${itemIndex}`
                        const panelId = `${baseId}-answer-${itemIndex}`

                        return (
                            <li key={item.question} data-reveal className={cn('border-b', dark ? 'border-bone/15' : 'border-charcoal/15')}>
                                <h3>
                                    <button
                                        id={buttonId}
                                        type='button'
                                        aria-expanded={expanded}
                                        aria-controls={panelId}
                                        onClick={() => setOpen(expanded ? null : itemIndex)}
                                        className='flex w-full items-start justify-between gap-6 py-6 text-left lg:py-7'
                                    >
                                        <span className='text-lg leading-snug sm:text-xl'>
                                            {item.question}
                                        </span>

                                        <span
                                            aria-hidden='true'
                                            className={cn(
                                                'mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-full border transition-[rotate,background-color] duration-500',
                                                dark ? 'border-bone/25' : 'border-charcoal/20',
                                                expanded && 'rotate-45'
                                            )}
                                        >
                                            <Plus className='size-4' />
                                        </span>
                                    </button>
                                </h3>

                                <div
                                    id={panelId}
                                    role='region'
                                    aria-labelledby={buttonId}
                                    aria-hidden={!expanded}
                                    className={cn(
                                        'grid transition-[grid-template-rows] duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]',
                                        expanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
                                    )}
                                >
                                    <div className='overflow-hidden'>
                                        <p className={cn('max-w-2xl pb-7 pr-14 text-[15px] leading-relaxed', dark ? 'text-bone/70' : 'text-charcoal/70')}>
                                            {item.answer}
                                        </p>
                                    </div>
                                </div>
                            </li>
                        )
                    })}
                </ul>
            </div>
        </section>
    )
}
